import { flatten, sortBy } from 'lodash-es';
import { useMemo } from 'react';

import type { MarketDataCardConfig, Security } from '@talos/kyoko';

import { CardViewEnum } from '../../../../../tokens/card';

export function useCardColumns(
  sortCards: boolean,
  isAddingCard: boolean,
  columns: MarketDataCardConfig[][],
  securitiesBySymbol: Map<string, Security>
) {
  return useMemo(() => {
    if (!sortCards || isAddingCard) {
      return columns;
    }

    const lastColumnIndex = columns?.reduceRight((res, el, index) => (el.length > 0 ? Math.max(res, index) : res), -1);
    const numberOfColumns = lastColumnIndex + 1;
    const allCards = flatten(columns);
    const filteredCards = allCards.filter(card => securitiesBySymbol.get(card.symbol ?? ''));
    const sortedCards = sortBy(filteredCards, card => {
      const marketSortKey =
        card.title ||
        (card.selectedMarketAccounts.length + card.selectedMarkets.length > 1
          ? 'Aggregate'
          : (card.selectedMarketAccounts[0] ?? card.selectedMarkets[0]));

      const security = securitiesBySymbol.get(card.symbol ?? '');

      const sortKey = [security?.BaseCurrency, security?.QuoteCurrency, marketSortKey];
      return sortKey;
    });
    let columnIndex = 0;
    let cardIndex = 0;

    const newColumns: MarketDataCardConfig[][] = Array.from({ length: numberOfColumns }).map(() => []);
    while (cardIndex < sortedCards.length) {
      const card = sortedCards[cardIndex];
      const column = newColumns[columnIndex % numberOfColumns];

      const columnHeight = column.reduce(
        (res, c) => res + (c.view === CardViewEnum.COMPACT ? 1 : c.view === CardViewEnum.NORMAL ? 2 : 3),
        0
      );

      if (columnHeight <= Math.floor(columnIndex / numberOfColumns)) {
        column.push(card);
        cardIndex += 1;
      }

      newColumns[columnIndex % numberOfColumns] = column;
      columnIndex += 1;
    }
    return newColumns;
  }, [columns, sortCards, isAddingCard, securitiesBySymbol]);
}
